import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './i18n';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import TimeEntries from './components/TimeEntries';
import Expenses from './components/Expenses';
import PhotoGallery from './components/PhotoGallery';
import ScheduleCalendar from './components/ScheduleCalendar';
import ReportsComplete from './components/ReportsComplete';
import Clients from './components/Clients';
import UserProfile from './components/UserProfile';
import ApproveHours from './components/ApproveHours';
import Users from './components/Users';
import Quotes from './components/Quotes';
import Invoices from './components/Invoices';
import PurchaseInvoices from './components/PurchaseInvoices';
import AccountantExport from './components/AccountantExport';
import Esquarcio from './components/Esquarcio';
import EmailTemplates from './components/EmailTemplates';
import EmailImportSettings from './components/EmailImportSettings';
import DocumentTermsSettings from './components/DocumentTermsSettings';
import Assistant from './components/Assistant';
import WhatsAppInbox from './components/WhatsAppInbox';
import Leads from './components/Leads';
import KlantenportaalUsers from './components/KlantenportaalUsers';
import ClientDetail from './components/ClientDetail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import PublicQuote from './components/PublicQuote';
import PublicInvoice from './components/PublicInvoice';
import Warranties from './components/Warranties';
import DamageReports from './components/DamageReports';
import PublicWarranty from './components/PublicWarranty';
import Layout from './components/Layout';
import ClientLogin from './client/ClientLogin';
import ClientRegister from './client/ClientRegister';
import ClientDashboard from './client/ClientDashboard';
import NewWorkRequest from './client/NewWorkRequest';
import WorkRequestDetail from './client/WorkRequestDetail';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import useVersionCheck from './hooks/useVersionCheck';
import './App.css';

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL || '';
// When deployed to a different domain (e.g. production), use current origin for API calls
const BACKEND_URL = configuredBackendUrl && !configuredBackendUrl.includes(window.location.hostname)
  ? window.location.origin
  : configuredBackendUrl;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  timeout: 25000, // 25 seconds timeout (before Cloudflare's 30s limit)
});

// Retry logic for failed requests
const retryRequest = async (error) => {
  const config = error.config;
  if (!config || config._retryCount >= 1) return Promise.reject(error);
  config._retryCount = (config._retryCount || 0) + 1;
  await new Promise(r => setTimeout(r, 1000));
  return axiosInstance(config);
};

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros de resposta
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on explicit 401 (token expired/invalid)
    // Don't redirect if the request is a login/register attempt
    if (error.response && error.response.status === 401) {
      const isAuthRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register');
      if (!isAuthRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    // Retry on network errors or 5xx (except 401)
    if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
      return retryRequest(error);
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance
        .get('/auth/me')
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch((error) => {
          // Só faz logout se for erro de autenticação (401)
          // Erros de conexão (500, network error) não devem fazer logout
          if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  // Detect new frontend deploys → show "Nieuwe versie beschikbaar" toast
  useVersionCheck(() => {
    toast(
      "Nieuwe versie beschikbaar",
      {
        description: "Klik op 'Vernieuwen' om de nieuwste versie te laden.",
        duration: Infinity,
        action: {
          label: 'Vernieuwen',
          onClick: () => window.location.reload(),
        },
        cancel: {
          label: 'Later',
          onClick: () => {},
        },
      }
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f6f8]">
        <div className="text-gray-600 text-xl">Carregando...</div>
      </div>
    );
  }

  const isBoekhouder = user?.role === 'boekhouder';
  const isEsquarcio = user?.role === 'esquarcio';
  const staff = user && !isBoekhouder && !isEsquarcio;
  const finance = user && (user.is_admin || isBoekhouder);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Client Portal Routes */}
          <Route path="/client" element={<ClientLogin />} />
          <Route path="/client/register" element={<ClientRegister />} />
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          <Route path="/client/new-request" element={<NewWorkRequest />} />
          <Route path="/client/request/:id" element={<WorkRequestDetail />} />

          {/* Public Offerte Signing — no auth required */}
          <Route path="/offerte/:token" element={<PublicQuote />} />
          <Route path="/factuur/:token" element={<PublicInvoice />} />
          <Route path="/garantie/:token" element={<PublicWarranty />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Employee System Routes */}
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? (
                isBoekhouder ? <Navigate to="/invoices" replace /> : isEsquarcio ? <Navigate to="/esquarcio" replace /> : (
                <Layout user={user} onLogout={handleLogout}>
                  <Dashboard user={user} />
                </Layout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/time-entries"
            element={
              staff ? (
                <Layout user={user} onLogout={handleLogout}>
                  <TimeEntries user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              staff ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ReportsComplete user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/clients"
            element={
              staff ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Clients user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/profile"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <UserProfile user={user} onLogout={handleLogout} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/photos"
            element={
              staff ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PhotoGallery />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/schedule"
            element={
              staff ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ScheduleCalendar user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/expenses"
            element={
              staff || isBoekhouder ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Expenses user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/approve-hours"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ApproveHours />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Users />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/quotes"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Quotes />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              finance ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Invoices user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/schade-rapporten"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DamageReports user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/warranties"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Warranties user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/purchase-invoices"
            element={
              finance ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PurchaseInvoices user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/accountant-export"
            element={
              finance ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AccountantExport user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/esquarcio"
            element={
              finance || isEsquarcio ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Esquarcio user={user} />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/email-templates"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <EmailTemplates />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/email-import"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <EmailImportSettings />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/aanvragen"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Leads />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/whatsapp"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <WhatsAppInbox />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/assistent"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Assistant />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/document-teksten"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DocumentTermsSettings />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/klantenportaal-users"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <KlantenportaalUsers />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/clients/:id"
            element={
              user && user.is_admin ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ClientDetail />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;